import type { PlanCode } from './prototypeTypes';

export type ClientBillingPlan = 'free' | 'lite' | 'pro' | 'max';
export type ClientPaidPlan = Exclude<ClientBillingPlan, 'free'>;

export type ClientPlanContext = {
	billingPlan: ClientBillingPlan;
	promotionalAccessPlan: ClientPaidPlan | null;
	effectiveAccessPlan: PlanCode;
	temporaryAccess: {
		tiers: Array<{ plan: ClientPaidPlan; endsAt: string }>;
		currentTierEndsAt: string;
		finalEndsAt: string;
	} | null;
};

const billingPlans = new Set<ClientBillingPlan>(['free', 'lite', 'pro', 'max']);
const paidPlans = new Set<ClientPaidPlan>(['lite', 'pro', 'max']);
const rank: Record<ClientBillingPlan, number> = { free: 0, lite: 1, pro: 2, max: 3 };

/**
 * The client must never infer a paid identity from the old viewer.plan field.
 * This validates the only PlanContext wire format before state is updated.
 */
export function parseClientPlanContext(value: unknown): ClientPlanContext | null {
	if (!value || typeof value !== 'object') return null;
	const source = value as Record<string, unknown>;
	if (typeof source.billingPlan !== 'string' || !billingPlans.has(source.billingPlan as ClientBillingPlan)) return null;
	if (!(source.promotionalAccessPlan === null || (typeof source.promotionalAccessPlan === 'string' && paidPlans.has(source.promotionalAccessPlan as ClientPaidPlan)))) return null;
	if (typeof source.effectiveAccessPlan !== 'string' || !billingPlans.has(source.effectiveAccessPlan as ClientBillingPlan)) return null;
	const billingPlan = source.billingPlan as ClientBillingPlan;
	const promotionalAccessPlan = source.promotionalAccessPlan as ClientPaidPlan | null;
	const effectiveAccessPlan = source.effectiveAccessPlan as ClientBillingPlan;
	if (effectiveAccessPlan !== (promotionalAccessPlan && rank[promotionalAccessPlan] > rank[billingPlan] ? promotionalAccessPlan : billingPlan)) return null;
	if (!promotionalAccessPlan) return source.temporaryAccess === null ? { billingPlan, promotionalAccessPlan, effectiveAccessPlan, temporaryAccess: null } : null;
	if (!source.temporaryAccess || typeof source.temporaryAccess !== 'object') return null;
	const temporary = source.temporaryAccess as Record<string, unknown>;
	if (!Array.isArray(temporary.tiers) || typeof temporary.currentTierEndsAt !== 'string' || typeof temporary.finalEndsAt !== 'string') return null;
	const tiers = temporary.tiers.flatMap((tier) => {
		if (!tier || typeof tier !== 'object') return [];
		const item = tier as Record<string, unknown>;
		return typeof item.plan === 'string' && paidPlans.has(item.plan as ClientPaidPlan) && typeof item.endsAt === 'string'
			? [{ plan: item.plan as ClientPaidPlan, endsAt: item.endsAt }]
			: [];
	});
	if (tiers.length !== temporary.tiers.length || !tiers.some((tier) => tier.plan === promotionalAccessPlan && tier.endsAt === temporary.currentTierEndsAt)) return null;
	return { billingPlan, promotionalAccessPlan, effectiveAccessPlan, temporaryAccess: { tiers, currentTierEndsAt: temporary.currentTierEndsAt, finalEndsAt: temporary.finalEndsAt } };
}

export function unauthenticatedClientPlanContext(): ClientPlanContext {
	return { billingPlan: 'free', promotionalAccessPlan: null, effectiveAccessPlan: 'visitor', temporaryAccess: null };
}

/** Returns the real next access level only when the current temporary tier expires within 24 hours. */
export function temporaryAccessDowngrade(context: ClientPlanContext, now = new Date().toISOString()): { endsAt: string; nextPlan: ClientBillingPlan } | null {
	const temporary = context.temporaryAccess;
	if (!temporary || context.effectiveAccessPlan === 'visitor') return null;
	const endsAtMs = Date.parse(temporary.currentTierEndsAt);
	const remainingMs = endsAtMs - Date.parse(now);
	if (!Number.isFinite(remainingMs) || remainingMs <= 0 || remainingMs > 86_400_000) return null;
	let nextPlan = context.billingPlan;
	for (const tier of temporary.tiers) {
		if (Date.parse(tier.endsAt) > endsAtMs && rank[tier.plan] > rank[nextPlan]) nextPlan = tier.plan;
	}
	return rank[nextPlan] < rank[context.effectiveAccessPlan as ClientBillingPlan] ? { endsAt: temporary.currentTierEndsAt, nextPlan } : null;
}

/**
 * Returns the strongest access that remains after the currently effective
 * promotional tier expires. Unlike the 24-hour downgrade warning, Profile
 * uses this for an always-accurate, compact "Then …" explanation.
 */
export function accessAfterTemporaryTier(context: ClientPlanContext): ClientBillingPlan {
	const temporary = context.temporaryAccess;
	if (!temporary || context.effectiveAccessPlan === 'visitor') return context.billingPlan;
	const currentEndsAt = Date.parse(temporary.currentTierEndsAt);
	if (!Number.isFinite(currentEndsAt)) return context.billingPlan;
	let nextPlan = context.billingPlan;
	for (const tier of temporary.tiers) {
		const tierEndsAt = Date.parse(tier.endsAt);
		if (Number.isFinite(tierEndsAt) && tierEndsAt > currentEndsAt && rank[tier.plan] > rank[nextPlan]) nextPlan = tier.plan;
	}
	return nextPlan;
}
